import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, FileText, Loader2, RotateCw, Trash2, Upload } from "lucide-react";
import { errMsg } from "../lib/api";
import { uploadFile, FILE_STATUS, ACCEPT } from "../lib/upload";

const MODE_LABEL = { extract: "read by AI", vision: "read by AI (image)", store: "stored only" };

export default function ProjectFiles({ project, api }) {
  const [d, setD] = useState(null);
  const [up, setUp] = useState(null);
  const ref = useRef(null);
  const load = useCallback(() => api.get(`/projects/${project.id}/files`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [api, project.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!d?.files.some((f) => f.status === "processing")) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [d, load]);
  const onFiles = async (list) => {
    for (const file of Array.from(list)) {
      setUp({ name: file.name, p: 0 });
      try { await uploadFile(api, project.id, file, (p) => setUp({ name: file.name, p })); } catch (e) { toast.error(errMsg(e)); }
    }
    setUp(null); load();
  };
  const download = async (f) => {
    try {
      const r = await api.get(`/projects/${project.id}/files/${f.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = f.filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Couldn't download this file. Please try again."); }
  };
  const act = (f, what) => {
    if (what === "delete" && !window.confirm(`Remove ${f.filename}?`)) return;
    (what === "retry" ? api.post(`/projects/${project.id}/files/${f.id}/retry`) : api.delete(`/projects/${project.id}/files/${f.id}`)).then(load).catch((e) => toast.error(errMsg(e)));
  };
  if (!d) return <p className="text-sm text-slate-500">Loading files…</p>;
  const groups = Object.entries(d.modes).reduce((acc, [e, m]) => ({ ...acc, [m]: [...(acc[m] || []), e] }), {});
  return (
    <div className="space-y-5" data-testid="project-files">
      <div className="card-lux flex flex-wrap items-center justify-between gap-4">
        <div><h3 className="text-2xl text-[#062a20]">Your files</h3>
          <p className="text-xs text-slate-500 mt-1">Max {d.max_mb} MB each. {Object.entries(groups).map(([m, es]) => `${es.map((e) => e.toUpperCase()).join(", ")}: ${MODE_LABEL[m]}`).join(" · ")}. Files are private to you and Altiora.</p></div>
        <input ref={ref} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} data-testid="files-input" />
        <button onClick={() => ref.current?.click()} disabled={!!up} className="btn-emerald" data-testid="files-upload-btn">{up ? <><Loader2 className="w-4 h-4 animate-spin" /> {up.p}%</> : <><Upload className="w-4 h-4" /> Upload files</>}</button>
      </div>
      {d.files.length === 0 ? <p className="text-sm text-slate-500" data-testid="files-empty">No files yet. Upload drawings, documents, spreadsheets or images to help the AI understand your requirements.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{d.files.map((f) => {
          const [label, cls] = FILE_STATUS[f.status] || [f.status, ""];
          return (
            <div key={f.id} className="card-lux !p-4 flex flex-col gap-2" data-testid={`file-${f.id}`}>
              <div className="flex items-start gap-3"><span className="w-10 h-10 rounded-lg bg-[#f4efe2] grid place-items-center text-[10px] font-bold text-[#0b3d2e]">{f.ext.toUpperCase()}</span>
                <div className="min-w-0"><p className="text-sm font-medium truncate" title={f.filename}>{f.filename}</p><p className="text-xs text-slate-500">{(f.size / 1024 / 1024).toFixed(2)} MB · {f.created_at.slice(0, 10)}</p></div></div>
              <p className={`text-xs font-semibold flex items-center gap-1 ${cls}`} data-testid={`file-status-${f.id}`}>{f.status === "processing" && <Loader2 className="w-3 h-3 animate-spin" />}<FileText className="w-3 h-3" />{label}{f.chars ? ` · ${f.chars.toLocaleString()} characters read` : ""}</p>
              {f.note && <p className="text-xs text-slate-600">{f.note}</p>}
              <div className="mt-auto flex gap-2 pt-1">
                <button onClick={() => download(f)} className="chip !py-1" data-testid={`file-download-${f.id}`}><Download className="w-3 h-3" /> Download</button>
                {f.status === "failed" && <button onClick={() => act(f, "retry")} className="chip !py-1" data-testid={`file-retry-${f.id}`}><RotateCw className="w-3 h-3" /> Retry</button>}
                <button onClick={() => act(f, "delete")} className="chip !py-1 ml-auto" aria-label="Remove file" data-testid={`file-delete-${f.id}`}><Trash2 className="w-3 h-3" /></button>
              </div>
            </div>);
        })}</div>)}
    </div>
  );
}
